import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppDatePicker } from '../../shared/components/AppDatePicker';
import { ScanningOverlay } from '../../shared/components/ScanningOverlay';
import { ApiError } from '../../api/httpClient';
import { getReturnRequestDetail, getPartnerDirectory, postComment, postResubmit, postReturnRequest } from '../../api/vipMpApi';
import { useToast } from '../../context/ToastContext';
import { useVipMpSession } from '../../context/VipMpSessionContext';
import type { ReturnRequestFormValues } from '../../types/vipMp';
import {
  buildApiPayload,
  defaultFormValues,
  lineTotalInUsd,
  managerEmailRequired,
  newLineItem,
} from './buildSubmitPayload';
import { mapDetailToFormValues } from './mapDetailToForm';

const GEO_OPTIONS = [
  { value: '', label: '-- Select Region --' },
  { value: 'NA', label: 'NA' },
  { value: 'EMEA', label: 'EMEA' },
  { value: 'LATAM', label: 'LATAM' },
  { value: 'APAC', label: 'APAC' },
  { value: 'JAPAN', label: 'JAPAN' }
];

const RETURN_TYPES = [
  { value: '', label: '-- Select Type --' },
  { value: 'Full Return', label: 'Full Return' },
  { value: 'Partial Return', label: 'Partial Return' }
];

const REASONS = [
  { value: '', label: '-- Select Reason --' },
  { value: 'Duplicate Order', label: 'Duplicate Order' },
  { value: 'Wrong Company/End user', label: 'Wrong Company/End user' },
  { value: 'Wrong Sku#/Product(s)', label: 'Wrong Sku#/Product(s)' },
  { value: 'Wrong QTY', label: 'Wrong QTY' },
  { value: 'Moved to another partner', label: 'Moved to another partner' },
  { value: 'AutoRenewal created in Error', label: 'AutoRenewal created in Error' },
  { value: 'VIP MP to ETLA Migration', label: 'VIP MP to ETLA Migration' }
];

const TIMEFRAMES = [
  { value: '', label: '-- Select Time Frame --' },
  { value: '15-30', label: '15-30 days: Adobe approval required, no Return fee (0%)' },
  { value: '31-60', label: '31-60 days: Adobe approval required, 25% Return fee' },
  { value: '61-90', label: '61-90 days: Adobe approval required, 50% Return fee' }
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'AUD'];

// const PARTNER_POLICY_NOTE =
//   'VIP Marketplace standard return policy allows Direct Partner orders to be returned fully or partially, but only by whole line item within a 14-days after the order date. As Adobe transitions to VIP Marketplace, we understand partners are working through the change and developing new processes, and there may be errors. Thus, as we continue to work through the transition to VIP Marketplace, Adobe is now considering exceptional return requests for VIP Marketplace later than 14 days after the order date. Adobe will review and may approve these returns as an exception to make additional accommodation for partners through the transition to VIP Marketplaces.';
const PARTNER_POLICY_NOTE =
  "The VIP Marketplace standard return policy allows Direct Partners to return orders in full or in part, but only as a whole line item within 14 days of the order date.  Return requests outside of this 14-day window may be submitted on an exception basis for Adobe's review. If approved, a return fee may be assessed based on the number of days after the order date.";

function isPartnerOrderNumberValid(value: string) {
  return /^16\d{8}$/.test(value.trim());
}

function isReplacementOrderValid(value: string) {
  return value.trim() === '' || /^16\d{8}$/.test(value.trim());
}

function isMarketplaceAccountIdValid(value: string) {
  return /^1005\d{6}$/.test(value.trim());
}

function isEmailLike(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isAdobeEmailLike(value: string) {
  const email = value.trim().toLowerCase();
  return email === '' || (isEmailLike(email) && email.endsWith('@adobe.com'));
}

function parseWholePositiveInteger(value: string): number | null {
  const trimmed = String(value || '').trim();
  if (!/^[1-9]\d*$/.test(trimmed)) return null;
  return Number(trimmed);
}

function normalizeQuantityInput(value: string): string {
  const digitsOnly = String(value || '').replace(/[^\d]/g, '');
  return digitsOnly.replace(/^0+(?=\d)/, '');
}

function fieldClassName(isValid: boolean, isInvalid = false) {
  return `form-control${isValid ? ' valid' : ''}${isInvalid ? ' invalid' : ''}`;
}

type FieldValidationErrors = Partial<Record<string, string>>;

type ValidationErrors = {
  step1: FieldValidationErrors;
  step2: FieldValidationErrors;
  step3: Record<string, FieldValidationErrors>;
  step3General?: string;
  step4: FieldValidationErrors;
};

function buildValidationErrors(values: ReturnRequestFormValues, updateMode: boolean): ValidationErrors {
  const step1: FieldValidationErrors = {};
  const step2: FieldValidationErrors = {};
  const step3: Record<string, FieldValidationErrors> = {};
  const step4: FieldValidationErrors = {};

  if (!values.partnerName.trim()) step1.partnerName = 'Partner name is required.';
  if (!values.contactName.trim()) step1.contactName = 'Contact name is required.';
  if (!values.contactEmail.trim()) step1.contactEmail = 'Contact email is required.';
  else if (!isEmailLike(values.contactEmail)) step1.contactEmail = 'Invalid contact email.';
  if (!isPartnerOrderNumberValid(values.orderNumber)) {
    step1.orderNumber = 'Order number must be 10 digits starting with 16.';
  }
  if (values.replacementOrder.trim() && !isReplacementOrderValid(values.replacementOrder)) {
    step1.replacementOrder = 'Replacement order must be empty or 10 digits starting with 16.';
  }
  if (!values.orderDate) step1.orderDate = 'Order date is required.';

  if (!isMarketplaceAccountIdValid(values.accountId)) {
    step2.accountId = 'Account ID must be 10 digits starting with 1005.';
  }
  if (!values.customerName.trim()) step2.customerName = 'Customer name is required.';
  if (!values.geoCode) step2.geoCode = 'GEO is required.';
  if (!values.returnTypeCode) step2.returnTypeCode = 'Return type is required.';
  if (!values.reasonCode) step2.reasonCode = 'Reason is required.';
  if (!values.timeframeCode) step2.timeframeCode = 'Time frame is required.';
  if (!values.managerName.trim()) step2.managerName = 'Adobe Channel Account Manager name is required.';
  if (values.managerEmail.trim() && !isAdobeEmailLike(values.managerEmail)) {
    step2.managerEmail = 'Must be an @adobe.com email address.';
  } else if (managerEmailRequired(values) && !values.managerEmail.trim()) {
    step2.managerEmail = 'Adobe Channel Account Manager email is required when return value exceeds $5,000 or local equivalent.';
  }
  if (lineTotalInUsd(values) > 5000 && !values.notifyCam) {
    step2.notifyCam = 'Please check "Notify account manager" for returns exceeding $5,000 or local equivalent. Adobe Channel Account Manager will be notified.';
  }

  let step3General = '';
  if (!values.lineItems.length) {
    step3General = 'At least one line item is required.';
  }
  values.lineItems.forEach((li) => {
    const errors: FieldValidationErrors = {};
    if (!li.productDescription.trim()) errors.productDescription = 'Product description is required.';
    if (!li.adobeSku.trim()) errors.adobeSku = 'SKU is required.';
    const price = parseFloat(li.unitPrice);
    if (!Number.isFinite(price) || price < 0.01) errors.unitPrice = 'Price must be at least 0.01.';
    const qty = parseWholePositiveInteger(li.quantity);
    if (qty == null || qty < 1) errors.quantity = 'Quantity must be a whole number >= 1.';
    if (Object.keys(errors).length) step3[li.id] = errors;
  });
  const cur = values.lineItems[0]?.currencyCode;
  if (cur && values.lineItems.some((x) => x.currencyCode !== cur)) {
    step3General = 'All line items must use the same currency as the first line.';
  }

  if (!updateMode) {
    if (!values.feeAcknowledged) step4.feeAcknowledged = 'Fee acknowledgment is required.';
    if (!values.accuracyConfirmed) step4.accuracyConfirmed = 'Accuracy confirmation is required.';
  }

  return { step1, step2, step3, step3General, step4 };
}

export type ReturnRequestWizardProps = {
  updateMode?: { returnRequestId: number; requestNumber: string } | null;
  updateOmResponse?: string;
  onUpdateOmResponseChange?: (value: string) => void;
  onClearUpdateMode?: () => void;
  onAfterSubmit?: () => void;
};

function PartnerCombobox({
  value, options, loading, disabled, onChange,
  placeholder = 'Adobe Direct Partner organization name'
}: {
  value: string; options: string[]; loading: boolean;
  disabled?: boolean; onChange: (v: string) => void; placeholder?: string;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen]   = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // Sync when parent sets value externally (e.g. updateMode seed)
  useEffect(() => { setQuery(value); }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q ? options.filter(o => o.toLowerCase().includes(q)) : options;
    return matches.slice(0, 50);
  }, [query, options]);

  // Scroll highlighted item into view whenever activeIdx changes
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const item = listRef.current.children[activeIdx] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  const select = (name: string) => {
    setQuery(name);
    onChange(name);
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); setActiveIdx(0); return; }
      setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      select(filtered[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  const handleBlur = () => setTimeout(() => {
    setOpen(false);
    setActiveIdx(-1);
    if (options.length > 0 && !options.some(o => o.toLowerCase() === query.trim().toLowerCase())) {
      setQuery(value);
    } else if (options.length === 0) {
      onChange(query);
    }
  }, 150);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        className="form-control"
        value={loading ? '' : query}
        placeholder={loading ? 'Loading partners…' : placeholder}
        disabled={disabled || loading}
        autoComplete="off"
        style={{ width: '100%' }}
        onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => { if (!loading && options.length > 0) setOpen(true); }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {open && filtered.length > 0 && (
        <div
          ref={listRef}
          style={{
            position: 'absolute', zIndex: 1000, left: 0, right: 0, top: '100%',
            background: '#fff', border: '1px solid #ccc', borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto'
          }}
        >
          {filtered.map((name, idx) => (
            <div
              key={name}
              onMouseDown={() => select(name)}
              onMouseEnter={() => setActiveIdx(idx)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 14,
                background: idx === activeIdx ? '#e3f2fd' : '#fff',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReturnRequestWizard({
  updateMode,
  updateOmResponse = '',
  onUpdateOmResponseChange,
  onClearUpdateMode,
  onAfterSubmit
}: ReturnRequestWizardProps) {
  const { showToast } = useToast();
  const session = useVipMpSession();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<ReturnRequestFormValues>(() => defaultFormValues());
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ requestNumber: string; returnRequestId: number } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [omAskReason, setOmAskReason] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [validationAttemptedStep, setValidationAttemptedStep] = useState(0);
  const [partnerList, setPartnerList] = useState<string[]>([]);
  const [partnerListLoading, setPartnerListLoading] = useState(true);

  useEffect(() => {
    getPartnerDirectory()
      .then(rows => setPartnerList(rows.map(r => r.partnerName).filter(Boolean)))
      .catch(() => {})
      .finally(() => setPartnerListLoading(false));
  }, []);

  useEffect(() => {
    if (!updateMode) {
      setValidationAttemptedStep(0);
      setForm((current) => ({
        ...current,
        partnerName: session.partnerOrgName,
        contactName: session.partnerContactName,
        contactEmail: session.partnerContactEmail
      }));
    }
  }, [session.partnerOrgName, session.partnerContactName, session.partnerContactEmail, updateMode]);

  useEffect(() => {
    let cancelled = false;
    setOmAskReason('');
    (async () => {
      if (!updateMode) return;
      setLoadErr(null);
      setValidationAttemptedStep(0);
      try {
        const detail = await getReturnRequestDetail(updateMode.returnRequestId);
        if (cancelled) return;
        setForm(mapDetailToFormValues(detail));
        setStep(1);
        setSuccess(null);
        const sorted = [...(detail.statusHistory ?? [])].sort((a, b) => {
          const t1 = a.ChangedUtc ? new Date(String(a.ChangedUtc)).getTime() : 0;
          const t2 = b.ChangedUtc ? new Date(String(b.ChangedUtc)).getTime() : 0;
          return t2 - t1;
        });
        for (const sh of sorted) {
          const code = String(sh.ToStatusCode ?? sh.toStatusCode ?? '').toUpperCase();
          const reason = String(sh.ChangeReason ?? sh.changeReason ?? '').trim();
          if (code === 'PENDING_PARTNER_ACTION' && reason) {
            setOmAskReason(reason.replace(/^Information requested from partner:\s*/i, '').trim());
            break;
          }
        }
      } catch (error) {
        if (!cancelled) setLoadErr(error instanceof Error ? error.message : 'Failed to load case');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [updateMode]);

  const lineTotal = useMemo(
    () => form.lineItems.reduce((sum, li) => sum + (parseFloat(li.unitPrice) || 0) * (parseWholePositiveInteger(li.quantity) || 0), 0),
    [form.lineItems]
  );

  const feePct = useMemo(() => {
    if (form.timeframeCode === '15-30') return 0;
    if (form.timeframeCode === '31-60') return 0.25;
    if (form.timeframeCode === '61-90') return 0.5;
    return 0;
  }, [form.timeframeCode]);

  const feeAmount = lineTotal * feePct;
  const orderNumberValid = isPartnerOrderNumberValid(form.orderNumber);
  const replacementOrderValid = isReplacementOrderValid(form.replacementOrder);
  const accountIdValid = isMarketplaceAccountIdValid(form.accountId);
  const contactNameValid = form.contactName.trim().length > 0;
  const contactEmailValid = isEmailLike(form.contactEmail);
  const managerNameValid = form.managerName.trim().length > 0;
  const managerEmailValid = isAdobeEmailLike(form.managerEmail);
  const validationErrors = useMemo(() => buildValidationErrors(form, !!updateMode), [form, updateMode]);
  const showStep1Errors = validationAttemptedStep >= 1;
  const showStep2Errors = validationAttemptedStep >= 2;
  const showStep3Errors = validationAttemptedStep >= 3;
  const showStep4Errors = validationAttemptedStep >= 4;

  const setField = <K extends keyof ReturnRequestFormValues>(key: K, value: ReturnRequestFormValues[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const goNext = () => {
    const errs = (
      step === 1
        ? Object.values(validationErrors.step1)
        : step === 2
          ? Object.values(validationErrors.step2)
          : step === 3
            ? Object.values(validationErrors.step3).flatMap((row) => Object.values(row))
            : []
    ).filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    if (errs.length) {
      setValidationAttemptedStep((current) => Math.max(current, step));
      showToast(errs[0], 'error');
      return;
    }
    if (step === 3 && (
      (managerEmailRequired(form) && !form.managerEmail.trim()) ||
      (lineTotalInUsd(form) > 5000 && !form.notifyCam)
    )) {
      setValidationAttemptedStep((current) => Math.max(current, 2));
      setStep(2);
      showToast('Adobe Channel Account Manager email and notification are required when return value exceeds $5,000 or local equivalent.', 'error');
      return;
    }
    setStep((current) => Math.min(4, current + 1));
  };

  const goBack = () => setStep((current) => Math.max(1, current - 1));

  const addLine = () => {
    const firstCur = form.lineItems[0]?.currencyCode || 'USD';
    const nextId = String(Math.max(0, ...form.lineItems.map((row) => parseInt(row.id, 10) || 0)) + 1);
    setForm((current) => ({
      ...current,
      lineItems: [...current.lineItems, newLineItem(nextId, firstCur)]
    }));
  };

  const removeLine = (id: string) => {
    if (form.lineItems.length <= 1) return;
    setForm((current) => ({
      ...current,
      lineItems: current.lineItems.filter((row) => row.id !== id)
    }));
  };

  const patchLine = (id: string, patch: Partial<ReturnRequestFormValues['lineItems'][number]>) => {
    setForm((current) => ({
      ...current,
      lineItems: current.lineItems.map((row) => (row.id === id ? { ...row, ...patch } : row))
    }));
  };

  const onFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const max = 5;
    const maxBytes = 10 * 1024 * 1024;
    const allowed = ['.pdf', '.xlsx', '.xls', '.doc', '.docx','.png','.jpg','.jpeg','.msg','.csv'];
    const next = [...form.attachments];
    Array.from(files).forEach((file) => {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (!allowed.includes(ext) || file.size > maxBytes) return;
      if (next.length >= max) return;
      next.push({ id: `${Date.now()}-${file.name}`, file });
    });
    setField('attachments', next);
  };

  const removeAttachment = (id: string) => {
    setField(
      'attachments',
      form.attachments.filter((row) => row.id !== id)
    );
  };

  const sendResponseComment = async () => {
    if (!updateMode || !updateOmResponse.trim()) return;
    setSendingComment(true);
    try {
      await postComment(updateMode.returnRequestId, {
        commentText: updateOmResponse.trim(),
        authorPersonaCode: 'PARTNER',
        authorName: form.contactName.trim(),
        authorEmail: form.contactEmail.trim(),
        visibleToPartner: true
      });
      onUpdateOmResponseChange?.('');
      showToast('Response sent.', 'success');
    } catch (error) {
      const msg = error instanceof ApiError ? error.message : 'Failed to send response.';
      showToast(msg, 'error');
    } finally {
      setSendingComment(false);
    }
  };

  const submit = async () => {
    const errs = [
      ...Object.values(validationErrors.step1),
      ...Object.values(validationErrors.step2),
      ...Object.values(validationErrors.step3).flatMap((row) => Object.values(row)),
      ...(updateMode ? [] : Object.values(validationErrors.step4))
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    if (errs.length) {
      setValidationAttemptedStep(4);
      showToast(errs[0], 'error');
      return;
    }
    setSubmitting(true);
    try {
      let payload = await buildApiPayload(form);
      payload = {
        ...payload,
        filedByName: session.analystName || undefined,
        filedByEmail: session.analystEmail || undefined,
        filedByPersona: 'PARTNER'
      };
      if (updateMode) {
        // Post any unsent response text as activity log comment (skip if partner already sent via the Send button)
        if (updateOmResponse.trim()) {
          await postComment(updateMode.returnRequestId, {
            commentText: updateOmResponse.trim(),
            authorPersonaCode: 'PARTNER',
            authorName: form.contactName.trim(),
            authorEmail: form.contactEmail.trim(),
            visibleToPartner: true
          });
        }
        // Request details: always the form's Additional Comments field — independent of response box
        await postResubmit(updateMode.returnRequestId, {
          submitterName: payload.submitterName,
          submitterEmail: payload.submitterEmail,
          filedByName: payload.filedByName,
          filedByEmail: payload.filedByEmail,
          filedByPersona: payload.filedByPersona,
          partnerComments: payload.partnerComments,
          managerName: payload.managerName,
          managerEmail: payload.managerEmail,
          adobeSalesOrderNumber: payload.adobeSalesOrderNumber,
          replacementOrderNumber: payload.replacementOrderNumber,
          orderDate: payload.orderDate,
          customerMarketplaceAccountId: payload.customerMarketplaceAccountId,
          customerName: payload.customerName,
          geoCode: payload.geoCode,
          returnTypeCode: payload.returnTypeCode,
          reasonCode: payload.reasonCode,
          timeframeCode: payload.timeframeCode,
          lineItems: payload.lineItems,
          attachments: payload.attachments
        });
        showToast(`Response submitted for ${updateMode.requestNumber}.`, 'success');
        onClearUpdateMode?.();
        onAfterSubmit?.();
        session.setPartnerProfile(form.partnerName.trim(), form.contactName.trim(), form.contactEmail.trim());
        setForm(defaultFormValues());
        setStep(1);
        return;
      }
      const created = await postReturnRequest(payload);
      setSuccess({ requestNumber: created.requestNumber, returnRequestId: created.returnRequestId });
      session.setPartnerProfile(form.partnerName.trim(), form.contactName.trim(), form.contactEmail.trim());
      showToast(`Request ${created.requestNumber} submitted successfully!`, 'success');
      onAfterSubmit?.();
    } catch (error) {
      const msg = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Submit failed';
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAfterSuccess = () => {
    setSuccess(null);
    setForm({
      ...defaultFormValues(),
      partnerName: session.partnerOrgName,
      contactName: session.partnerContactName,
      contactEmail: session.partnerContactEmail
    });
    setStep(1);
    setValidationAttemptedStep(0);
  };

  if (success) {
    return (
      <div className="form-step active">
        <div className="card">
          <div className="card-header">
            <h2>Request submitted</h2>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 16, marginBottom: 12 }}>
              Your request ID: <strong id="success-request-id">{success.requestNumber}</strong>
            </p>
            <p style={{ color: '#666', marginBottom: 20 }}>We will email you when the status changes.</p>
            <button type="button" className="btn btn-primary" onClick={resetAfterSuccess}>
              Submit another request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {submitting && form.attachments.length > 0 && <ScanningOverlay />}
      {updateMode && (
        <div className="update-mode-banner">
          <div className="update-banner-content">
            <div className="update-banner-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <div className="update-banner-text">
              <strong>Action Required for {updateMode.requestNumber}</strong>
              <p className="update-reason">
                {omAskReason || 'Please review and update the requested information.'}
              </p>
            </div>
            <div className="update-banner-actions">
              <button type="button" className="btn btn-outline" onClick={() => onClearUpdateMode?.()} disabled={submitting}>
                Cancel
              </button>
            </div>
          </div>
          <div className="update-response-section">
            <label>Your response (optional)</label>
            <textarea
              value={updateOmResponse}
              onChange={(e) => onUpdateOmResponseChange?.(e.target.value)}
              placeholder="Provide any additional context or explanation for your updates..."
            />
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => void sendResponseComment()}
              disabled={sendingComment || !updateOmResponse.trim()}
              style={{ marginTop: 8 }}
            >
              {sendingComment ? 'Sending…' : 'Send response'}
            </button>
          </div>
        </div>
      )}

      {loadErr && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ color: '#c62828' }}>
            {loadErr}
          </div>
        </div>
      )}

      <div className="form-stepper">
        {[1, 2, 3, 4].map((n, idx) => (
          <React.Fragment key={n}>
            {idx > 0 && <div className={`stepper-connector ${step > n - 1 ? 'completed' : step === n ? 'half' : ''}`} />}
            <div className={`stepper-item ${step === n ? 'active' : ''} ${step > n ? 'completed' : ''}`}>
              <div className="step-number">{n}</div>
              <div className="step-label">
                {n === 1 ? 'Partner & Order' : n === 2 ? 'Customer & Return' : n === 3 ? 'Line Items' : 'Review & Submit'}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="form-step active">
          <div className="card">
            <div className="card-header">
              <h2>
                <span className="icon">1</span> Partner & Order Information
              </h2>
            </div>
            <div className="card-body">
              <div
                style={{
                  background: '#f5f5f5',
                  borderLeft: '3px solid #eb1000',
                  padding: '14px 18px',
                  borderRadius: 4,
                  marginBottom: 24,
                  fontSize: 12.5,
                  color: '#444',
                  lineHeight: 1.7
                }}
              >
                {PARTNER_POLICY_NOTE}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Adobe Direct Partner Name <span className="required">*</span>
                  </label>
                  <PartnerCombobox
                    value={form.partnerName}
                    options={partnerList}
                    loading={partnerListLoading}
                    disabled={!!updateMode}
                    onChange={v => setField('partnerName', v)}
                  />
                  <span className={`form-hint ${showStep1Errors && validationErrors.step1.partnerName ? 'error' : ''}`}>
                    {showStep1Errors && validationErrors.step1.partnerName
                      ? validationErrors.step1.partnerName
                      : updateMode ? 'Cannot be changed after submission' : ''}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Partner Contact (Submitter) <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <input
                      className={fieldClassName(contactNameValid)}
                      value={form.contactName}
                      onChange={(e) => setField('contactName', e.target.value)}
                      disabled={!!updateMode}
                    />
                    {!updateMode && contactNameValid && <span className="validation-icon" style={{ color: '#4caf50' }}>✓</span>}
                  </div>
                  <span className={`form-hint ${showStep1Errors && validationErrors.step1.contactName ? 'error' : ''}`}>
                    {showStep1Errors && validationErrors.step1.contactName
                      ? validationErrors.step1.contactName
                      : updateMode ? 'Cannot be changed after submission' : 'Name of the person submitting this request on behalf of the partner organization.'}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Partner Contact Email <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <input
                      type="email"
                      className={fieldClassName(contactEmailValid, form.contactEmail.trim().length > 0 && !contactEmailValid)}
                      value={form.contactEmail}
                      onChange={(e) => setField('contactEmail', e.target.value)}
                      placeholder="your.email@company.com"
                      disabled={!!updateMode}
                    />
                    {!updateMode && contactEmailValid && <span className="validation-icon" style={{ color: '#4caf50' }}>✓</span>}
                  </div>
                  <span className={`form-hint ${showStep1Errors && validationErrors.step1.contactEmail ? 'error' : ''}`}>
                    {showStep1Errors && validationErrors.step1.contactEmail
                      ? validationErrors.step1.contactEmail
                      : updateMode ? 'Cannot be changed after submission' : ''}
                  </span>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Adobe Sales Order Number <span className="required">*</span>{' '}
                    <span className="info-tip" title="10-digit order number starting with 16">
                      ?
                    </span>
                  </label>
                  <input
                    className={fieldClassName(orderNumberValid, form.orderNumber.length > 0 && !orderNumberValid)}
                    maxLength={10}
                    value={form.orderNumber}
                    onChange={(e) => setField('orderNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="16XXXXXXXX"
                  />
                  <span className={`form-hint ${showStep1Errors && validationErrors.step1.orderNumber ? 'error' : orderNumberValid ? 'success' : ''}`}>
                    {showStep1Errors && validationErrors.step1.orderNumber
                      ? validationErrors.step1.orderNumber
                      : orderNumberValid
                        ? 'Valid'
                        : 'Must be 10 digits starting with 16'}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Replacement Order{' '}
                    <span className="info-tip" title="Enter if this return is related to a replacement order">
                      ?
                    </span>
                  </label>
                  <input
                    className={fieldClassName(replacementOrderValid, form.replacementOrder.length > 0 && !replacementOrderValid)}
                    maxLength={10}
                    value={form.replacementOrder}
                    onChange={(e) => setField('replacementOrder', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="16XXXXXXXX (optional)"
                  />
                  <span className={`form-hint ${showStep1Errors && validationErrors.step1.replacementOrder ? 'error' : ''}`}>
                    {showStep1Errors && validationErrors.step1.replacementOrder
                      ? validationErrors.step1.replacementOrder
                      : 'Optional - same format as order number'}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Order Date <span className="required">*</span>
                  </label>
                  <AppDatePicker
                    value={form.orderDate}
                    className="form-control"
                    onChange={(val) => setField('orderDate', val)}
                  />
                  <span className={`form-hint ${showStep1Errors && validationErrors.step1.orderDate ? 'error' : ''}`}>
                    {showStep1Errors && validationErrors.step1.orderDate
                      ? validationErrors.step1.orderDate
                      : 'Select the original order date'}
                  </span>
                </div>
              </div>
            </div>
            <div className="form-navigation">
              <p style={{ fontSize: 12, color: '#888', margin: 0, maxWidth: 480 }}>
                Return requests due to non-payment by a customer or reseller are the responsibility of the Direct Partner only.
              </p>
              <button type="button" className="btn btn-primary btn-lg" onClick={goNext}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="form-step active">
          <div className="card">
            <div className="card-header">
              <h2>
                <span className="icon">2</span> Customer & Return Details
              </h2>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Customer Marketplace Account ID <span className="required">*</span>{' '}
                    <span className="info-tip" title="10-digit ID starting with 1005">
                      ?
                    </span>
                  </label>
                  <input
                    className={fieldClassName(accountIdValid, form.accountId.length > 0 && !accountIdValid)}
                    maxLength={10}
                    value={form.accountId}
                    onChange={(e) => setField('accountId', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="1005XXXXXX"
                  />
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.accountId ? 'error' : accountIdValid ? 'success' : ''}`}>
                    {showStep2Errors && validationErrors.step2.accountId
                      ? validationErrors.step2.accountId
                      : accountIdValid
                        ? 'Valid'
                        : 'Must begin with 1005 and be 10 digits'}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Customer Name <span className="required">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.customerName}
                    onChange={(e) => setField('customerName', e.target.value)}
                    placeholder="Enter customer company name"
                  />
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.customerName ? 'error' : ''}`}>
                    {showStep2Errors && validationErrors.step2.customerName ? validationErrors.step2.customerName : ''}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    GEO <span className="required">*</span>
                  </label>
                  <select className="form-control" value={form.geoCode} onChange={(e) => setField('geoCode', e.target.value)}>
                    {GEO_OPTIONS.map((option) => (
                      <option key={option.value || 'empty'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.geoCode ? 'error' : ''}`}>
                    {showStep2Errors && validationErrors.step2.geoCode ? validationErrors.step2.geoCode : ''}
                  </span>
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '24px 0' }} />

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Return Type <span className="required">*</span>
                  </label>
                  <select className="form-control" value={form.returnTypeCode} onChange={(e) => setField('returnTypeCode', e.target.value)}>
                    {RETURN_TYPES.map((option) => (
                      <option key={option.value || 'empty'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.returnTypeCode ? 'error' : ''}`}>
                    {showStep2Errors && validationErrors.step2.returnTypeCode ? validationErrors.step2.returnTypeCode : ''}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Reason for Request <span className="required">*</span>
                  </label>
                  <select className="form-control" value={form.reasonCode} onChange={(e) => setField('reasonCode', e.target.value)}>
                    {REASONS.map((option) => (
                      <option key={option.value || 'empty'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.reasonCode ? 'error' : ''}`}>
                    {showStep2Errors && validationErrors.step2.reasonCode ? validationErrors.step2.reasonCode : ''}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Time Frame Outside Policy <span className="required">*</span>{' '}
                    <span className="info-tip" title="Fee percentage is based on days past return window">
                      ?
                    </span>
                  </label>
                  <select className="form-control" value={form.timeframeCode} onChange={(e) => setField('timeframeCode', e.target.value)}>
                    {TIMEFRAMES.map((option) => (
                      <option key={option.value || 'empty'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.timeframeCode ? 'error' : ''}`}>
                    {showStep2Errors && validationErrors.step2.timeframeCode ? validationErrors.step2.timeframeCode : ''}
                  </span>
                  <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0 0' }}>
                    Returns +90 days past the original order date are not allowed.
                  </p>
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-group">
                  <label>
                    Adobe Account Manager <span className="required">*</span>{' '}
                    <span className="info-tip" title="Name of the Adobe Account Manager. Required.">
                      ?
                    </span>
                  </label>
                  <div className="input-wrapper">
                    <input
                      className={fieldClassName(managerNameValid)}
                      value={form.managerName}
                      onChange={(e) => setField('managerName', e.target.value)}
                      placeholder="Enter account manager name"
                    />
                    {managerNameValid && <span className="validation-icon" style={{ color: '#4caf50' }}>✓</span>}
                  </div>
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.managerName ? 'error' : managerNameValid ? 'success' : ''}`}>
                    {showStep2Errors && validationErrors.step2.managerName
                      ? validationErrors.step2.managerName
                      : managerNameValid
                        ? 'Valid'
                        : 'Required - Adobe Channel Account Manager name'}
                  </span>
                </div>
                <div className="form-group">
                  <label>
                    Adobe Account Manager Email{' '}
                    <span
                      className="info-tip"
                      title="Adobe Account Manager to be notified when 'Notify account manager' is selected. Not for approval."
                    >
                      ?
                    </span>
                  </label>
                  <input
                    type="email"
                    className={fieldClassName(managerEmailValid, form.managerEmail.trim().length > 0 && !managerEmailValid)}
                    value={form.managerEmail}
                    onChange={(e) => setField('managerEmail', e.target.value)}
                    placeholder="name@adobe.com"
                  />
                  <span className={`form-hint ${showStep2Errors && validationErrors.step2.managerEmail ? 'error' : ''}`}>
                    {showStep2Errors && validationErrors.step2.managerEmail
                      ? validationErrors.step2.managerEmail
                      : managerEmailRequired(form)
                          ? managerEmailValid
                            ? 'Required - Notify account manager is selected.'
                            : 'Must be an @adobe.com email address.'
                        : managerEmailValid
                          ? ''
                          : 'Must be an @adobe.com email address.'}
                  </span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer', fontSize: 13, color: '#444' }}>
                    <input
                      type="checkbox"
                      checked={form.notifyCam}
                      onChange={(e) => setField('notifyCam', e.target.checked)}
                      style={{ width: 15, height: 15, accentColor: '#eb1000', flexShrink: 0 }}
                    />
                    Notify account manager of this return
                  </label>
                  {showStep2Errors && validationErrors.step2.notifyCam && (
                    <span className="form-hint error">{validationErrors.step2.notifyCam}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                Back
              </button>
              <button type="button" className="btn btn-primary btn-lg" onClick={goNext}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="form-step active">
          <div className="card">
            <div className="card-header">
              <h2>
                <span className="icon">3</span> Line Items & Attachments
              </h2>
            </div>
            <div className="card-body">
              <p className="form-hint line-items-currency-callout">
                <strong>Single currency per request.</strong> The currency you select in the first line item applies to all line items in this request. To submit in another currency, please submit a separate request.
              </p>
              {showStep3Errors && validationErrors.step3General && (
                <span className="form-hint error" style={{ display: 'block', marginBottom: 12 }}>
                  {validationErrors.step3General}
                </span>
              )}

              {form.lineItems.map((li, index) => (
                <div className="line-item-card" key={li.id}>
                  <div className="line-item-header">
                    <span className="line-item-number">Line Item {index + 1}</span>
                    {form.lineItems.length > 1 && (
                      <span className="line-item-remove" onClick={() => removeLine(li.id)}>
                        Remove
                      </span>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Product Description <span className="required">*</span>
                      </label>
                      <input
                        className="form-control line-item-product"
                        maxLength={500}
                        value={li.productDescription}
                        onChange={(e) => patchLine(li.id, { productDescription: e.target.value })}
                        placeholder="e.g., Creative Cloud All Apps - Teams"
                      />
                      <span className={`form-hint ${showStep3Errors && validationErrors.step3[li.id]?.productDescription ? 'error' : ''}`}>
                        {showStep3Errors && validationErrors.step3[li.id]?.productDescription
                          ? validationErrors.step3[li.id]?.productDescription
                          : 'Required - Free text, max 500 characters'}
                      </span>
                    </div>
                    <div className="form-group">
                      <label>
                        Adobe SKU <span className="required">*</span>
                      </label>
                      <input
                        className="form-control line-item-sku"
                        maxLength={50}
                        value={li.adobeSku}
                        onChange={(e) => patchLine(li.id, { adobeSku: e.target.value })}
                        placeholder="e.g., 65297752BA01A12"
                      />
                      <span className={`form-hint ${showStep3Errors && validationErrors.step3[li.id]?.adobeSku ? 'error' : ''}`}>
                        {showStep3Errors && validationErrors.step3[li.id]?.adobeSku
                          ? validationErrors.step3[li.id]?.adobeSku
                          : 'Required - Free text, max 50 characters'}
                      </span>
                    </div>
                    <div className="form-group">
                      <label>Deploy To ID</label>
                      <input
                        className="form-control line-item-deploy-id"
                        maxLength={20}
                        value={li.deployToId}
                        onChange={(e) => patchLine(li.id, { deployToId: e.target.value })}
                        placeholder="e.g. 14XXXXXXXX"
                      />
                      <span className="form-hint">Optional - Typically begins with 14. For multiple deploy locations.</span>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Adobe partner price <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control line-item-price"
                        value={li.unitPrice}
                        onChange={(e) => {
                          const filtered = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                          patchLine(li.id, { unitPrice: filtered });
                        }}
                        placeholder="0.00"
                      />
                      <span className={`form-hint ${showStep3Errors && validationErrors.step3[li.id]?.unitPrice ? 'error' : ''}`}>
                        {showStep3Errors && validationErrors.step3[li.id]?.unitPrice
                          ? validationErrors.step3[li.id]?.unitPrice
                          : 'Required - Numeric, 2 decimal places'}
                      </span>
                    </div>
                    <div className="form-group">
                      <label>
                        Currency <span className="required">*</span>
                      </label>
                      <select
                        className="form-control line-item-currency"
                        value={li.currencyCode}
                        disabled={index > 0}
                        onChange={(e) => {
                          const value = e.target.value;
                          setForm((current) => ({
                            ...current,
                            lineItems: current.lineItems.map((row) => ({ ...row, currencyCode: value }))
                          }));
                        }}
                      >
                        {CURRENCIES.map((currency) => (
                          <option key={currency} value={currency}>
                            {currency}
                          </option>
                        ))}
                      </select>
                      <span className="form-hint">Required - locks currency for all line items</span>
                    </div>
                    <div className="form-group">
                      <label>
                        Quantity <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control line-item-qty"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="off"
                        value={li.quantity}
                        onChange={(e) => patchLine(li.id, { quantity: normalizeQuantityInput(e.target.value) })}
                        onKeyDown={(e) => {
                          if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="1"
                      />
                      <span className={`form-hint ${showStep3Errors && validationErrors.step3[li.id]?.quantity ? 'error' : ''}`}>
                        {showStep3Errors && validationErrors.step3[li.id]?.quantity
                          ? validationErrors.step3[li.id]?.quantity
                          : 'Required - Whole number, minimum 1'}
                      </span>
                    </div>
                  </div>
                  <div className="line-item-total">
                    Line Total:{' '}
                    <span className="line-total-amount">
                      {((parseFloat(li.unitPrice) || 0) * (parseWholePositiveInteger(li.quantity) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              ))}

              <p className="form-hint" style={{ marginBottom: 10 }}>
                Enter line items in the form below. Do not use Excel upload for line item data; use attachments only for supporting documents. Maximum 20 line items.
              </p>
              <button type="button" className="add-line-item-btn" onClick={addLine}>
                + Add Another Line Item
              </button>

              <div className="fee-calculator" style={{ marginTop: 20 }}>
                <h3>Fee Calculation</h3>
                <div className="fee-row">
                  <span>Return Sum ({form.lineItems.length} item{form.lineItems.length === 1 ? '' : 's'})</span>
                  <span className="amount">{lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="fee-row">
                  <span>Time Frame</span>
                  <span className="amount">{form.timeframeCode || '--'}</span>
                </div>
                <div className="fee-row">
                  <span>Fee Percentage</span>
                  <span className="amount">{Math.round(feePct * 100)}%</span>
                </div>
                <div className="fee-row fee-total">
                  <span>Fee Amount</span>
                  <span className="amount">{feeAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <hr className="section-divider" />
              <h3 className="section-title">Attachments (Optional)</h3>
              <input type="file" multiple accept=".pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.msg,.csv" onChange={(e) => onFiles(e.target.files)} />
              <ul style={{ marginTop: 12, paddingLeft: 18 }}>
                {form.attachments.map((attachment) => (
                  <li key={attachment.id}>
                    {attachment.file.name}{' '}
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => removeAttachment(attachment.id)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>

              <h3 className="section-title">Additional Comments</h3>
              <textarea
                className="form-control"
                rows={4}
                value={form.partnerComments}
                onChange={(e) => setField('partnerComments', e.target.value)}
                placeholder="Any additional information to support your request..."
              />
            </div>
            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                Back
              </button>
              <button type="button" className="btn btn-primary btn-lg" onClick={goNext}>
                Review submission
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="form-step active">
          <div className="card">
            <div className="card-header">
              <h2>
                <span className="icon">4</span> Review & Submit
              </h2>
            </div>
            <div className="card-body">
              <p style={{ color: '#666', marginBottom: 20 }}>Please review your submission details before submitting.</p>

              <div className="review-section">
                <h4>Partner & Order Information</h4>
                <div className="review-grid">
                  <div className="review-item">
                    <div className="label">Adobe Direct Partner Name</div>
                    <div className="value">{form.partnerName}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Partner Contact (Submitter)</div>
                    <div className="value">{form.contactName}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Partner Contact Email</div>
                    <div className="value">{form.contactEmail}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Order Number</div>
                    <div className="value">{form.orderNumber}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Replacement Order</div>
                    <div className="value">{form.replacementOrder || '-'}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Order Date</div>
                    <div className="value">{form.orderDate ? (() => { const d = new Date(form.orderDate + 'T00:00:00'); const m = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][d.getMonth()]; return `${String(d.getDate()).padStart(2,'0')} ${m} ${d.getFullYear()}`; })() : ''}</div>
                  </div>
                </div>
              </div>

              <div className="review-section">
                <h4>Customer & Return Details</h4>
                <div className="review-grid">
                  <div className="review-item">
                    <div className="label">Customer</div>
                    <div className="value">{form.customerName}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Account ID</div>
                    <div className="value">{form.accountId}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">GEO</div>
                    <div className="value">{form.geoCode}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Return Type</div>
                    <div className="value">{form.returnTypeCode}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Reason</div>
                    <div className="value">{form.reasonCode}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Time Frame</div>
                    <div className="value">{form.timeframeCode}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Adobe Account Manager</div>
                    <div className="value">{form.managerName}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Adobe Account Manager Email</div>
                    <div className="value">{form.managerEmail || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="review-section">
                <h4>Line Items</h4>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Price</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lineItems.map((li) => (
                      <tr key={li.id}>
                        <td>{li.productDescription}</td>
                        <td>{li.adobeSku}</td>
                        <td>
                          {(parseFloat(li.unitPrice) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>{li.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!updateMode && (
                <>
                  <div className="acknowledgment-box">
                    <label>
                      <input type="checkbox" checked={form.feeAcknowledged} onChange={(e) => setField('feeAcknowledged', e.target.checked)} />
                      <p>
                        I understand that the Return Fee Amount of <strong>{feeAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {form.lineItems[0]?.currencyCode || 'USD'}</strong> above will be deducted from Direct <b>Partner's</b> quarterly attainment, during the quarter when the return is accepted.
                      </p>
                    </label>
                    {showStep4Errors && validationErrors.step4.feeAcknowledged && (
                      <span className="form-hint error" style={{ display: 'block', marginTop: 6 }}>
                        {validationErrors.step4.feeAcknowledged}
                      </span>
                    )}
                  </div>
                  <div className="acknowledgment-box" style={{ marginTop: 12 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={form.accuracyConfirmed}
                        onChange={(e) => setField('accuracyConfirmed', e.target.checked)}
                      />
                      <p>
                        I confirm that the details I have submitted are accurate and true to the best of my understanding.
                      </p>
                    </label>
                    {showStep4Errors && validationErrors.step4.accuracyConfirmed && (
                      <span className="form-hint error" style={{ display: 'block', marginTop: 6 }}>
                        {validationErrors.step4.accuracyConfirmed}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                Back
              </button>
              <button type="button" className="btn btn-primary btn-lg" disabled={submitting} onClick={submit}>
                {updateMode ? 'Submit response' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

